import './VoiceLogDrawer.css';
import type { VoiceLogEntry } from '../../hooks/useVoiceLog';

/**
 * V2 Voice GM (2026-06-02) — Voice Log drawer panel.
 *
 * 5th drawer panel in ScenarioRunner (sibling to Stats/History/Roll/Audio).
 * Renders the chronological voice-log entries for the current campaign,
 * tagged by role (player / gm / system). V2 only routes player STT
 * transcripts; V3 adds GM replies + system tool-call records.
 */

export interface VoiceLogDrawerProps {
  entries: VoiceLogEntry[];
  onClear: () => void;
}

const ROLE_LABEL: Record<VoiceLogEntry['role'], string> = {
  player: 'YOU',
  gm: 'GM',
  system: 'SYS',
};

export function VoiceLogDrawer({ entries, onClear }: VoiceLogDrawerProps) {
  return (
    <aside
      className="br-scenario-drawer br-voice-log-drawer"
      data-testid="br-scenario-drawer-voice-log"
      aria-label="Voice log"
    >
      <header>
        <h2 className="br-display">Voice Log</h2>
        <p className="br-label">
          STT transcripts this campaign. V3 will add GM replies + tool-call records.
        </p>
      </header>
      {entries.length === 0 && (
        <p
          className="br-label br-voice-log-empty"
          data-testid="br-voice-log-empty"
        >
          No voice turns yet. Click 🎤 on a paragraph to record one.
        </p>
      )}
      {entries.length > 0 && (
        <ul className="br-voice-log-list" data-testid="br-voice-log-list">
          {entries.map((e) => (
            <li
              key={e.id}
              className="br-voice-log-entry"
              data-role={e.role}
              data-testid={`br-voice-log-entry-${e.id}`}
            >
              <span className={`br-voice-log-role br-voice-log-role-${e.role} br-label`}>
                {ROLE_LABEL[e.role]}
              </span>
              <p className="br-body">{e.text}</p>
            </li>
          ))}
        </ul>
      )}
      {entries.length > 0 && (
        <section className="br-scenario-drawer-section">
          <button
            type="button"
            className="br-voice-log-clear"
            data-testid="br-voice-log-clear"
            onClick={onClear}
          >
            Clear log
          </button>
        </section>
      )}
    </aside>
  );
}

export default VoiceLogDrawer;
